import type { WalkingDistance } from "../../@types/mapviewxs";
import markerComponent from "../map-marker/index.marko";
import userLocationMarker from "../map-user-location-marker/index.marko";
import { ACTIVE_MARKER_Z_INDEX, MARKER_Z_INDEX, USER_LOCATION_MARKER_Z_INDEX } from "./const";

import { extractPosition } from "./helpers";
import type {
  FindMarkerIndexParams,
  LatLng,
  MarkerLibrary,
  MarkerRendererOptions,
  MarkerWithCircle,
  RenderMarkerOptions,
  UserLocationMarker,
} from "./types";

export function renderMarker({
  map,
  markerLibrary,
  points,
  selectionState,
  watchlistState,
  isPreviewCardEnabled,
  handlePreviewClose,
  defaultPreviewPosition,
  itemCardControls,
  mapAccessibilityTexts,
  locality,
  localizedContent,
  watchInstance,
  watchListItems,
  handleWatchListItemToggle,
  position: providedPosition,
}: RenderMarkerOptions) {
  const position = providedPosition || extractPosition(points[0].geometry);
  if (position.lng === 0) {
    // The backend returns 0 for the longitude of some points
    // which makes the cluster algorithm to not include the marker in a cluster.
    // Altering the latitude for 1 decimal will not change the position of the marker on the map.
    // This is a workaround to avoid that until we have a solution from Google.
    position.lng = 0.000001;
  }

  const { component, node } = markerRenderer({
    points,
    selectionState,
    watchlistState,
    handlePreviewClose,
    defaultPreviewPosition,
    itemCardControls,
    isPreviewCardEnabled,
    mapAccessibilityTexts,
    locality,
    localizedContent,
    watchInstance,
    watchListItems,
    handleWatchListItemToggle,
  });
  const marker = new markerLibrary.AdvancedMarkerElement({
    map,
    position,
    content: node,
    zIndex: selectionState === "selected" ? ACTIVE_MARKER_Z_INDEX : MARKER_Z_INDEX,
  });

  return {
    marker,
    component,
  };
}

function markerRenderer({
  points,
  selectionState,
  watchlistState,
  handlePreviewClose,
  defaultPreviewPosition,
  itemCardControls,
  isPreviewCardEnabled,
  mapAccessibilityTexts,
  locality,
  localizedContent,
  watchInstance,
  watchListItems,
  handleWatchListItemToggle,
}: MarkerRendererOptions) {
  const markerEl = markerComponent.renderSync({
    selectionState,
    watchlistState,
    animationDelay: 0,
    itemCards: points ? points : [],
    itemCardControls,
    onPreviewClose: handlePreviewClose,
    defaultPreviewPosition,
    isPreviewCardEnabled,
    mapAccessibilityTexts,
    locality,
    watchInstance,
    localizedContent,
    watchListItems,
    handleWatchListItemToggle,
  });

  // this is needed otherwise marko will complain about the markerEl not being appended to the DOM when updating the component
  const dummy = document.createElement("div");
  markerEl.appendTo(dummy);

  return {
    node: dummy,
    component: markerEl.getComponent(),
  };
}

export function highlightMarker(point: MarkerWithCircle): MarkerWithCircle {
  if (point?.component?.input) {
    if (point.component.input.selectionState !== "selected") {
      point.component.input.selectionState = "highlighted";
    }
    point.component.forceUpdate();
  }
  return point;
}

export function watchMarker(point: MarkerWithCircle): MarkerWithCircle {
  if (point?.component?.input) {
    if (point.component.input.watchlistState !== "watched") {
      point.component.input.watchlistState = "watched";
      point.component.forceUpdate();
    }
  }
  return point;
}

export function unwatchMarker(point: MarkerWithCircle): MarkerWithCircle {
  if (point?.component?.input) {
    if (point.component.input.watchlistState !== "unwatched") {
      point.component.input.watchlistState = "unwatched";
      point.component.forceUpdate();
    }
  }
  return point;
}

export function unHighlightMarker(point: MarkerWithCircle): MarkerWithCircle {
  if (point?.component?.input) {
    if (point.component.input.selectionState === "highlighted") {
      point.component.input.selectionState = "unselected";
      if (point.circle) {
        point.circle.setMap(null);
        point.circle = undefined;
      }
      point.component.forceUpdate();
    }
  }
  return point;
}

export function unHighlightMarkers(points: MarkerWithCircle[]): MarkerWithCircle[] {
  return points
    .filter(
      (point) => point.circle || point.component?.input?.selectionState === "selected" || point.component?.input?.selectionState === "highlighted",
    )
    .map((point) => unHighlightMarker(point));
}

export function selectMarker(point: MarkerWithCircle): MarkerWithCircle {
  if (point?.component?.input) {
    point.component.input.selectionState = "selected";
    if ("zIndex" in point.marker) {
      point.marker.zIndex = ACTIVE_MARKER_Z_INDEX;
    }
    point.component.forceUpdate();
  }
  return point;
}

export function unselectMarker(point: MarkerWithCircle): MarkerWithCircle {
  if (point?.component?.input) {
    point.component.input.selectionState = "unselected";
    if ("zIndex" in point.marker) {
      point.marker.zIndex = MARKER_Z_INDEX;
    }
    point.component.forceUpdate();
    if (point.circle) {
      point.circle.setMap(null);
      point.circle = undefined;
    }
  }
  return point;
}

export function unselectMarkers(points: MarkerWithCircle[]): MarkerWithCircle[] {
  return points
    .filter(
      (point) => point.circle || point.component?.input?.selectionState === "selected" || point.component?.input?.selectionState === "highlighted",
    )
    .map((point) => unselectMarker(point));
}

export function renderUserLocationMarker(
  map: google.maps.Map,
  markerLibrary: MarkerLibrary,
  position: LatLng,
  walkingDistance: WalkingDistance,
): UserLocationMarker {
  const markerEl = userLocationMarker.renderSync({ marker: walkingDistance?.marker, overlayRadius: 0 });
  const marker = new markerLibrary.AdvancedMarkerElement({
    map,
    position,
    content: markerEl.getNode(),
    zIndex: USER_LOCATION_MARKER_Z_INDEX,
  });
  // this is needed otherwise marko will complain about the markerEl not being appended to the DOM when updating the component
  const dummy = document.createElement("div");
  markerEl.appendTo(dummy);
  return { marker, component: markerEl.getComponent() as UserLocationMarker["component"] };
}

export function updateWalkingDistanceOverlayRadius(marker: UserLocationMarker, radius: number) {
  const { input } = marker.component;
  if ("overlayRadius" in input && input.overlayRadius !== radius) {
    input.overlayRadius = radius;
    marker.component.forceUpdate();
  }
}

export function toggleWalkingDistanceTourtip(marker: UserLocationMarker, isOpen: boolean, placement: "top" | "bottom" = "bottom") {
  const { state } = marker.component;
  if (state?.isTourtipOpen !== isOpen) {
    marker.component.setState({ isTourtipOpen: isOpen, placement });
    marker.component.forceUpdate();
  }
}

export function findMarkerIndex({ itemIndex, listingId, hasFilters, points }: FindMarkerIndexParams): {
  markerIndex: number;
  originalIndex: number;
} {
  let markerIndex = -1;
  let originalIndex = itemIndex;

  if (listingId) {
    markerIndex = points.findIndex((point) => point.component.input.itemCards?.[0].itemCard?.card.listingId === listingId);

    if (markerIndex !== -1) {
      originalIndex = points[markerIndex].originalIndex ?? markerIndex;
      return { markerIndex, originalIndex };
    }
  }

  if (!hasFilters) {
    markerIndex = itemIndex;
    originalIndex = itemIndex;
  } else {
    markerIndex = points.findIndex((point) => (point.originalIndex ?? points.indexOf(point)) === originalIndex);
  }

  return { markerIndex, originalIndex };
}
